"use client";

/**
 * Step 3 — Review. Credit breakdown from `useEnrollmentEstimate`:
 * already-revealed contacts cost nothing, new reveals cost 2 credits each, and
 * `credits_required` is the upper bound ("up to N credits"). Preview is
 * hard-gated: disabled while the estimate loads, when the org can't cover the
 * worst case, when the cohort overflows the 50-profile cap, or when it's empty.
 */

import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { StepFooter, StepScrollArea } from "@/components/wizard";
import { pluralize } from "@/lib/string/pluralize";
import { cn } from "@/lib/utils";

import { ENROLLMENT_ACCENT_TEXT, ENROLLMENT_PRIMARY_BTN } from "../../lib/theme";
import { useConfigureWizard } from "./context";

const CREDITS_PER_REVEAL = 2;

export function StepReview() {
  const {
    estimate,
    estimateLoading,
    estimateError,
    cohortTooLarge,
    cohortEmpty,
    previewDisabled,
    insufficientFrom402,
    startPreview,
    setStep,
  } = useConfigureWizard();

  const balanceAfter = estimate ? estimate.balance - estimate.credits_required : 0;
  const insufficient = (estimate && !estimate.sufficient) || insufficientFrom402;
  const shortfall = estimate
    ? Math.max(0, estimate.credits_required - estimate.balance)
    : 0;

  return (
    <>
      <StepScrollArea contentClassName="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          Review the credit cost before generating previews.
        </p>

        {cohortTooLarge ? (
          <div className="flex items-center gap-2.5 rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
            <Icon
              name="IconExclamationCircle"
              className="size-4 text-destructive"
            />
            <span className="text-xs text-muted-foreground">
              Select up to 50 profiles to enroll at once.
            </span>
          </div>
        ) : cohortEmpty ? (
          <div className="flex items-center gap-2.5 rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
            <Icon
              name="IconExclamationCircle"
              className="size-4 text-destructive"
            />
            <span className="text-xs text-muted-foreground">
              Select at least one profile to enroll.
            </span>
          </div>
        ) : estimateLoading ? (
          <div className="flex flex-col gap-2" aria-label="Estimating credits">
            <div className="h-20 animate-pulse rounded-lg bg-foreground/4" />
            <div className="h-11 animate-pulse rounded-lg bg-foreground/4" />
          </div>
        ) : estimateError || !estimate ? (
          <div className="flex items-center gap-2.5 rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
            <Icon
              name="IconExclamationCircle"
              className="size-4 text-destructive"
            />
            <span className="text-xs text-muted-foreground">
              Couldn&apos;t estimate the credit cost. Go back and try again.
            </span>
          </div>
        ) : (
          <>
            {/* Credit breakdown */}
            <div className="flex flex-col gap-1 rounded-lg border border-border bg-contrast/[0.02] p-2.5 text-[13px]">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  Profiles to enroll
                </span>
                <span className="font-medium text-foreground tabular-nums">
                  {estimate.total.toLocaleString()}
                </span>
              </div>
              {estimate.to_reveal > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    New contact reveals ({estimate.to_reveal} &times;{" "}
                    {CREDITS_PER_REVEAL})
                  </span>
                  <span className="font-medium text-foreground tabular-nums">
                    {estimate.to_reveal * CREDITS_PER_REVEAL}{" "}
                    {pluralize(
                      estimate.to_reveal * CREDITS_PER_REVEAL,
                      "credit",
                    )}
                  </span>
                </div>
              )}
              {estimate.already_revealed > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Already revealed ({estimate.already_revealed})
                  </span>
                  <span className="font-medium text-foreground">-</span>
                </div>
              )}
              <div className="mt-0.5 flex items-center justify-between border-t border-border pt-1.5">
                <span className="font-medium text-foreground">Total</span>
                <span className="font-semibold text-destructive tabular-nums">
                  up to {estimate.credits_required.toLocaleString()}{" "}
                  {pluralize(estimate.credits_required, "credit")}
                </span>
              </div>
            </div>

            {/* Balance — before → after */}
            <div className="flex items-center justify-between rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
              <span className="text-xs font-medium text-muted-foreground">
                Changes to your balance
              </span>
              <span className="flex items-center gap-2 text-sm">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Icon
                    name="IconCoin1"
                    size={13}
                    className={ENROLLMENT_ACCENT_TEXT}
                  />
                  <span className="font-medium tabular-nums">
                    {estimate.balance.toLocaleString()}
                  </span>
                </span>
                <Icon
                  name="IconArrowRight"
                  size={13}
                  className="text-foreground opacity-40"
                />
                <span
                  className={cn(
                    "flex items-center gap-1",
                    insufficient ? "text-destructive" : "text-heading",
                  )}
                >
                  <Icon
                    name="IconCoin1"
                    size={13}
                    className={ENROLLMENT_ACCENT_TEXT}
                  />
                  <span className="font-semibold tabular-nums">
                    {balanceAfter.toLocaleString()}
                  </span>
                </span>
              </span>
            </div>

            {insufficient && (
              <div className="flex items-center gap-2.5 rounded-lg border border-border bg-contrast/2 px-3 py-2.5">
                <div className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1 text-xs font-medium text-destructive">
                    <Icon name="IconExclamationCircle" className="size-4" />
                    Not enough credits
                  </span>
                  <span className="text-xs text-muted-foreground">
                    You need{" "}
                    <b className="font-medium text-foreground">
                      {shortfall.toLocaleString()} more{" "}
                      {pluralize(shortfall, "credit")}
                    </b>{" "}
                    to enroll.{" "}
                    <a
                      href="/settings/billing"
                      className="font-medium underline hover:opacity-80"
                    >
                      Purchase credits
                    </a>
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </StepScrollArea>
      <StepFooter>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setStep(2)}
        >
          Back
        </Button>
        <SubmitButton
          type="button"
          size="sm"
          onClick={startPreview}
          disabled={previewDisabled}
          icon={<Icon name="IconPaperPlane" size={14} />}
          className={ENROLLMENT_PRIMARY_BTN}
        >
          Preview
        </SubmitButton>
      </StepFooter>
    </>
  );
}
